import { Box, Typography } from "@mui/material";
import axios, { AxiosResponse, CancelTokenSource } from "axios";
import { last } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import useGlobalDialog from "./useGlobalDialog";

type AxiosResponseAwaited<T> =
    T extends PromiseLike<AxiosResponse<infer U>> ? U : T;

export enum Status {
    idle,
    initLoading,
    loading,
    success,
    error
}

export const isLoading = (status: Status) =>
    status === Status.loading || status === Status.initLoading;

const useApiRequest = <
    F extends (...arg: any) => Promise<AxiosResponse<T> | any>,
    T = AxiosResponseAwaited<ReturnType<F>>
>(
    serviceAPI: F,
    useToaster: { success: boolean; error: boolean } = {
        success: false,
        error: true
    }
) => {
    const [response, setResponse] = useState<T>();
    const [status, setStatus] = useState<Status>(Status.initLoading);
    const [error, setError] = useState<any>();

    const { open } = useGlobalDialog();

    const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);

    useEffect(() => {
        return () => {
            // Cleanup function to cancel the request when the component unmounts
            if (cancelTokenSourceRef.current) {
                cancelTokenSourceRef.current.cancel(
                    "Component is being unmounted"
                );
            }
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (status === Status.initLoading) {
                setStatus(Status.idle);
            }
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [status]);

    const action = useCallback(
        async (...params: [...Parameters<F>, { notLoading: boolean }?]) => {
            try {
                const args = params as any[];
                const lastParams = last(args);
                if (!lastParams?.notLoading) {
                    setStatus(Status.loading);
                }
                setError(undefined);

                // Create a new cancel token for each request
                cancelTokenSourceRef.current = axios.CancelToken.source();

                let result;
                if (useToaster.success) {
                    result = await toast.promise(serviceAPI(...args), {
                        pending: "Request is pending",
                        success: "Request resolved 👌",
                        error: "Request rejected 🤯"
                    });
                } else {
                    result = await toast.promise(serviceAPI(...args), {
                        error: "Request rejected 🤯"
                    });
                }
                setResponse(result.data);
                setResponse((prev) => {
                    setTimeout(() => {
                        setStatus(Status.success);
                    }, 400);
                    return prev;
                });
                return result.data as T;
            } catch (error) {
                if (error instanceof Error) {
                    setStatus(Status.error);
                    setError(error);
                } else {
                    setStatus(Status.error);
                    setError(error);
                }
                open(
                    "Internal Error",
                    <Box>
                        <Typography>Sorry for internal error occurs</Typography>
                        <Typography>
                            Please Contact us at{" "}
                            <a
                                href={`mailto://contact@sati.co.th?subject=Bug report from Impress&body=Error Message:\n${JSON.stringify(
                                    error
                                )}`}
                            >
                                contact@sati.co.th
                            </a>
                        </Typography>
                    </Box>
                );
                throw error;
            }
        },
        [serviceAPI, useToaster.success]
    );

    return [action, response, status, error, setResponse] as const;
};

export default useApiRequest;
